import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { payments } from '@lawbee/domain';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Field,
  FormError,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDate,
  formatDateTime,
  formatMoney,
} from '@lawbee/ui';
import { Ban, Download, FileText, HandCoins, MoreHorizontal, Plus, Send, Settings } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { PlanGate } from '../../../components/PlanGate';
import { api, apiUrl, errorMessage, qs } from '../../../lib/api';
import { dollarsToCents, humanize, useApiMutation } from '../../../lib/hooks';
import { can, useMe } from '../../../lib/me';
import type { AccountsResponse, Destination, Invoice, MatterSummary, Payment, PaymentKind } from '../../../lib/types';
import { CopyField, DestinationBadge, InvoiceStatusBadge } from './shared';

const KIND_SHORT: Record<PaymentKind, string> = { retainer: 'Retainer', invoice: 'Invoice', consultation_fee: 'Consultation fee' };

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

export function PaymentsPage() {
  return (
    <PlanGate feature="payments">
      <PaymentsInner />
    </PlanGate>
  );
}

function PaymentsInner() {
  const me = useMe();
  const [newOpen, setNewOpen] = useState(false);
  return (
    <div>
      <PageHeader
        title="Payments"
        description="Retainers, invoices and consultation fees, routed to your trust or operating account."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <a href={apiUrl('/api/payments/ledger.csv')} download>
                <Download /> Export ledger CSV
              </a>
            </Button>
            {can(me, 'payments_admin') ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/settings/payments">
                  <Settings /> Payment settings
                </Link>
              </Button>
            ) : null}
            {can(me, 'client_work') ? (
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Plus /> New invoice
              </Button>
            ) : null}
          </>
        }
      />
      <Alert variant="info" title="Trust accounting" className="mb-6">
        {payments.TRUST_COMPLIANCE_NOTICE}
      </Alert>
      <Tabs defaultValue="invoices">
        <TabsList aria-label="Payments views">
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          <InvoicesTab onNew={() => setNewOpen(true)} />
        </TabsContent>
        <TabsContent value="payments">
          <PaymentsTab />
        </TabsContent>
      </Tabs>
      <NewInvoiceDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

function InvoicesTab({ onNew }: { onNew: () => void }) {
  const me = useMe();
  const [status, setStatus] = useState('');
  const q = useQuery({ queryKey: ['invoices', status], queryFn: () => api.get<{ invoices: Invoice[] }>(`/api/payments/invoices${qs({ status })}`) });
  const [payTarget, setPayTarget] = useState<Invoice | null>(null);
  const [voidTarget, setVoidTarget] = useState<Invoice | null>(null);
  const [sentLink, setSentLink] = useState<{ invoice: Invoice; payUrl: string } | null>(null);

  const send = useApiMutation((inv: Invoice) => api.post<{ ok: boolean; payUrl: string }>(`/api/payments/invoices/${inv.id}/send`), {
    success: 'Payment link sent',
    invalidate: [['invoices']],
    onSuccess: (data, inv) => setSentLink({ invoice: inv, payUrl: data.payUrl }),
  });
  const voidInv = useApiMutation((inv: Invoice) => api.post(`/api/payments/invoices/${inv.id}/void`), {
    success: 'Invoice voided',
    invalidate: [['invoices'], ['dashboard']],
    onSuccess: () => setVoidTarget(null),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label htmlFor="inv-status" className="text-sm text-muted-foreground">
          Status
        </label>
        <Select id="inv-status" className="w-40" value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_FILTERS} />
      </div>
      {q.isLoading ? <LoadingState label="Loading invoices" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.invoices.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText />}
            title={status ? `No ${status} invoices` : 'No invoices yet'}
            description="Create an invoice or retainer request and send the client a secure payment link."
            action={
              can(me, 'client_work') ? (
                <Button onClick={onNew}>
                  <Plus /> New invoice
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : null}
      {q.data && q.data.invoices.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Payer</TableHead>
                <TableHead>Matter</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="figures whitespace-nowrap font-medium">{inv.number}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {KIND_SHORT[inv.kind] ?? humanize(inv.kind)}
                    <span className="block max-w-56 truncate text-xs text-muted-foreground">{inv.description}</span>
                  </TableCell>
                  <TableCell>
                    <DestinationBadge destination={inv.destination} />
                  </TableCell>
                  <TableCell className="max-w-48">
                    <span className="block truncate">{inv.payerName ?? '—'}</span>
                    {inv.payerEmail ? <span className="block truncate text-xs text-muted-foreground">{inv.payerEmail}</span> : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {inv.matterId && inv.matterNumber ? (
                      <Link to="/matters/$id" params={{ id: inv.matterId }} className="figures text-accent-foreground hover:underline">
                        {inv.matterNumber}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="figures whitespace-nowrap text-right">{formatMoney(inv.amountCents)}</TableCell>
                  <TableCell className="figures whitespace-nowrap text-right">{formatMoney(inv.paidCents)}</TableCell>
                  <TableCell>
                    <InvoiceStatusBadge status={inv.status} />
                  </TableCell>
                  <TableCell className="figures whitespace-nowrap">{inv.dueOn ? formatDate(inv.dueOn) : '—'}</TableCell>
                  <TableCell className="text-right">
                    {inv.status === 'open' || inv.status === 'draft' ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-8" aria-label={`Actions for invoice ${inv.number}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {can(me, 'client_work') ? (
                            <DropdownMenuItem icon={<Send />} onSelect={() => send.mutate(inv)}>
                              Send payment link
                            </DropdownMenuItem>
                          ) : null}
                          {can(me, 'payments_admin') && inv.status === 'open' ? (
                            <DropdownMenuItem icon={<HandCoins />} onSelect={() => setPayTarget(inv)}>
                              Record offline payment
                            </DropdownMenuItem>
                          ) : null}
                          {can(me, 'client_work') ? (
                            <DropdownMenuItem icon={<Ban />} destructive onSelect={() => setVoidTarget(inv)}>
                              Void
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      <RecordPaymentDialog invoice={payTarget} onClose={() => setPayTarget(null)} />
      <ConfirmDialog
        open={!!voidTarget}
        onOpenChange={(o) => !o && setVoidTarget(null)}
        title={`Void invoice ${voidTarget?.number ?? ''}?`}
        description="The payment link stops working and the invoice can no longer be paid. This cannot be undone."
        confirmLabel="Void invoice"
        destructive
        loading={voidInv.isPending}
        onConfirm={() => voidTarget && voidInv.mutate(voidTarget)}
      />
      <Dialog open={!!sentLink} onOpenChange={(o) => !o && setSentLink(null)}>
        <DialogContent
          title="Payment link sent"
          description={sentLink ? `We emailed the link for invoice ${sentLink.invoice.number}${sentLink.invoice.payerEmail ? ` to ${sentLink.invoice.payerEmail}` : ''}.` : undefined}
          footer={<Button onClick={() => setSentLink(null)}>Done</Button>}
        >
          {sentLink ? <CopyField label="Payment link" value={sentLink.payUrl} description="Anyone with this link can pay the invoice." /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecordPaymentDialog({ invoice, onClose }: { invoice: Invoice | null; onClose: () => void }) {
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      {invoice ? <RecordPaymentForm invoice={invoice} onClose={onClose} /> : null}
    </Dialog>
  );
}

function RecordPaymentForm({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const balance = invoice.amountCents - invoice.paidCents;
  const [amount, setAmount] = useState((balance / 100).toFixed(2));
  const [method, setMethod] = useState('etransfer');
  const [error, setError] = useState<string | null>(null);
  const record = useApiMutation((body: { amountCents: number; method: string }) => api.post(`/api/payments/invoices/${invoice.id}/record-payment`, body), {
    success: 'Payment recorded',
    invalidate: [['invoices'], ['payments-list'], ['dashboard']],
    onSuccess: onClose,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cents = dollarsToCents(amount);
    if (cents <= 0) return setError('Enter an amount.');
    if (cents > balance) return setError(`That is more than the balance owing (${formatMoney(balance)}).`);
    setError(null);
    record.mutate({ amountCents: cents, method });
  };
  return (
    <DialogContent
      title={`Record payment for ${invoice.number}`}
      description={
        <>
          Balance owing <span className="figures">{formatMoney(balance)}</span>. It is recorded to the {invoice.destination === 'trust' ? 'trust' : 'operating'} account.
        </>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={record.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="record-payment-form" loading={record.isPending}>
            Record payment
          </Button>
        </>
      }
    >
      <form id="record-payment-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Amount (CAD)" htmlFor="rp-amount" required error={error}>
          <Input id="rp-amount" inputMode="decimal" className="figures" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Method" htmlFor="rp-method">
          <Select
            id="rp-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            options={[
              { value: 'etransfer', label: 'Interac e-Transfer' },
              { value: 'cheque', label: 'Cheque' },
              { value: 'cash', label: 'Cash' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Field>
      </form>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// New invoice
// ---------------------------------------------------------------------------

function NewInvoiceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? <NewInvoiceForm onClose={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function NewInvoiceForm({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const accounts = useQuery({ queryKey: ['payment-accounts'], queryFn: () => api.get<AccountsResponse>('/api/payments/accounts') });
  const matters = useQuery({ queryKey: ['matters', 'open'], queryFn: () => api.get<{ matters: MatterSummary[] }>('/api/matters?status=open') });
  const [kind, setKind] = useState<PaymentKind>('retainer');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [matterId, setMatterId] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ invoice: Invoice; payUrl: string } | null>(null);

  const routing = accounts.data?.routing ?? me.settings.paymentRouting;
  const destination: Destination = routing[kind] ?? 'operating';

  const create = useApiMutation(
    (body: Record<string, unknown>) => api.post<{ invoice: Invoice; payUrl: string }>('/api/payments/invoices', body),
    {
      success: (d) => `Invoice ${d.invoice.number} created`,
      invalidate: [['invoices'], ['dashboard']],
      onSuccess: (d) => setCreated(d),
    },
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    const cents = dollarsToCents(amount);
    if (description.trim().length < 2) next.description = 'Describe what this payment is for.';
    if (cents < 100) next.amount = 'Enter an amount of at least $1.00.';
    if (payerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail)) next.payerEmail = 'Enter a valid email.';
    if (sendNow && !payerEmail && !matterId) next.payerEmail = 'Add the payer email (or pick a matter whose client has one) to send the link.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setFormError(null);
    create.mutate(
      {
        kind,
        description: description.trim(),
        amountCents: cents,
        payerName: payerName.trim() || null,
        payerEmail: payerEmail.trim() || null,
        dueOn: dueOn || null,
        matterId: matterId || null,
        send: sendNow,
      },
      { onError: (err) => setFormError(errorMessage(err)) },
    );
  };

  if (created) {
    return (
      <DialogContent title={`Invoice ${created.invoice.number} created`} footer={<Button onClick={onClose}>Done</Button>}>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            <span className="figures">{formatMoney(created.invoice.amountCents)}</span> to your {created.invoice.destination === 'trust' ? 'trust' : 'operating'} account.{' '}
            {sendNow ? 'We emailed the payment link to the payer.' : 'Copy the link below and send it to your client.'}
          </p>
          <CopyField label="Payment link" value={created.payUrl} />
        </div>
      </DialogContent>
    );
  }

  const matterOptions = (matters.data?.matters ?? []).map((m) => ({ value: m.id, label: `${m.number} · ${m.title}${m.clientName ? ` (${m.clientName})` : ''}` }));

  return (
    <DialogContent
      title="New invoice"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="new-invoice-form" loading={create.isPending}>
            {sendNow ? 'Create and send' : 'Create invoice'}
          </Button>
        </>
      }
    >
      <form id="new-invoice-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Type" htmlFor="ni-kind">
          <Select
            id="ni-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as PaymentKind)}
            options={payments.PAYMENT_KINDS.map((k) => ({ value: k, label: payments.PAYMENT_KIND_LABELS[k] }))}
          />
        </Field>
        <Alert variant={destination === 'trust' ? 'info' : 'success'}>
          This goes to your <span className="font-medium text-foreground">{destination === 'trust' ? 'trust account' : 'operating account'}</span>
          {accounts.data?.accounts[destination]?.label ? ` (${accounts.data.accounts[destination]?.label})` : ''}.
        </Alert>
        <Field label="Description" htmlFor="ni-desc" required error={errors.description}>
          <Input id="ni-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Retainer for separation agreement" aria-invalid={!!errors.description || undefined} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount (CAD)" htmlFor="ni-amount" required error={errors.amount}>
            <Input id="ni-amount" inputMode="decimal" className="figures" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!!errors.amount || undefined} />
          </Field>
          <Field label="Due date" htmlFor="ni-due">
            <Input id="ni-due" type="date" className="figures" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </Field>
        </div>
        <Field label="Matter" htmlFor="ni-matter" description="Optional. The matter's client becomes the payer when you leave payer details blank.">
          <Select id="ni-matter" value={matterId} onChange={(e) => setMatterId(e.target.value)} options={matterOptions} placeholder={matters.isLoading ? 'Loading matters' : 'No matter'} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Payer name" htmlFor="ni-payer">
            <Input id="ni-payer" value={payerName} onChange={(e) => setPayerName(e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Payer email" htmlFor="ni-email" error={errors.payerEmail}>
            <Input id="ni-email" type="email" value={payerEmail} onChange={(e) => setPayerEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors.payerEmail || undefined} />
          </Field>
        </div>
        <Checkbox label="Email the payment link now" checked={sendNow} onCheckedChange={(v) => setSendNow(v === true)} />
        <FormError message={formError} />
      </form>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// Payments list
// ---------------------------------------------------------------------------

type PaymentRow = Payment & { payerName?: string | null };

function PaymentsTab() {
  const q = useQuery({ queryKey: ['payments-list'], queryFn: () => api.get<{ payments: PaymentRow[] }>('/api/payments/payments') });
  if (q.isLoading) return <LoadingState label="Loading payments" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const list = q.data?.payments ?? [];
  if (list.length === 0) {
    return (
      <Card>
        <EmptyState icon={<HandCoins />} title="No payments yet" description="Card payments and offline payments you record appear here." />
      </Card>
    );
  }
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Method</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="figures whitespace-nowrap">{formatDateTime(p.createdAt)}</TableCell>
              <TableCell className="whitespace-nowrap">
                <span className="figures font-medium">{p.invoiceNumber}</span>
                {p.payerName ? <span className="block text-xs text-muted-foreground">{p.payerName}</span> : null}
              </TableCell>
              <TableCell className="whitespace-nowrap">{KIND_SHORT[p.kind] ?? humanize(p.kind)}</TableCell>
              <TableCell>
                <DestinationBadge destination={p.destination} />
              </TableCell>
              <TableCell className="whitespace-nowrap">{p.method === 'etransfer' ? 'e-Transfer' : humanize(p.method)}</TableCell>
              <TableCell className="figures whitespace-nowrap text-right">{formatMoney(p.amountCents)}</TableCell>
              <TableCell className="whitespace-nowrap">{humanize(p.status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
